/**
 * Admin/CloudtraqGds/Index — GDS Ops Command Center main screen.
 *
 * Rendered inside AdminLayout (applied via application.js resolve).
 * The left nav sidebar and hamburger toggle live in AdminLayout — not here.
 *
 * Exports: useOpsMonitor (imported by SkillFeed, DispatchBoard, etc.)
 */

import { router } from '@inertiajs/react'
import { useEffect, useRef, useState, useCallback } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 5)  return 'just now'
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SKILL_COLORS = {
  pnr_ingest:       '#2196F3',
  ava_insights:     '#9C27B0',
  irop_event_push:  '#F44336',
  msp_dispatch:     '#FF9800',
  vuuoffer_push:    '#00BCD4',
  attestation_push: '#4CAF50',
  dispatch_status:  '#607D8B',
}

const STATUS_COLORS  = { success: '#4CAF50', degraded: '#FF9800', error: '#F44336' }
const RISK_COLORS    = { critical: '#F44336', high: '#FF9800', moderate: '#FFC107', low: '#4CAF50' }
const CARRIER_NAMES  = { B6: 'JetBlue', DL: 'Delta', UA: 'United', AA: 'American', WN: 'Southwest' }
const CARRIER_COLORS_M = { B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2' }

// ─── useOpsMonitor ─────────────────────────────────────────────────────────────
// Exported so sub-pages (SkillFeed, DispatchBoard, etc.) can share this hook.
// Connects to OpsMonitorChannel via native ActionCable WebSocket.
// Auto-reconnects up to MAX_RETRIES with exponential backoff.

export function useOpsMonitor({ tenantKey, onEvent }) {
  const wsRef      = useRef(null)
  const retryRef   = useRef(0)
  const onEventRef = useRef(onEvent)
  useEffect(() => { onEventRef.current = onEvent }, [onEvent])

  useEffect(() => {
    let unmounted = false
    const MAX_RETRIES = 5

    function connect() {
      if (unmounted) return
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const ws = new WebSocket(`${protocol}//${window.location.host}/cable`)
      wsRef.current = ws

      ws.onopen = () => {
        retryRef.current = 0
        ws.send(JSON.stringify({
          command:    'subscribe',
          identifier: JSON.stringify({ channel: 'OpsMonitorChannel', tenant_key: tenantKey }),
        }))
      }
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data)
          if (d.type === 'ping') return
          if (d.message?.event) onEventRef.current(d.message.event, d.message.payload)
        } catch { /* ignore parse errors */ }
      }
      ws.onclose = () => {
        if (unmounted) return
        if (retryRef.current < MAX_RETRIES) {
          retryRef.current += 1
          setTimeout(connect, 2000 * retryRef.current)
        }
      }
    }

    connect()
    return () => { unmounted = true; wsRef.current?.close() }
  }, [tenantKey])
}

// ─── HelpModal ───────────────────────────────────────────────────────────────

const HELP_SECTIONS = [
  {
    title: '📊 KPI Tiles',
    items: [
      { term: 'Active PNRs',       def: 'Total cloudTraQ PNR records in the system. A PNR (Passenger Name Record) is one reservation segment — airline, hotel, or car rental. Click to see high-risk ones.' },
      { term: 'High Risk',         def: 'PNRs where AVA scored congestion_index > 0.6 — passengers flagged as likely to miss a connection. Turns red when > 0.' },
      { term: 'Active Dispatches', def: 'DispatchTasks in progress (Queued, Worker Assigned, or En Route). Click to open the full Kanban board.' },
      { term: 'ADK Calls (1h)',    def: 'IBM watsonx ADK skill invocations in the last hour across all 7 skill types. A spike often signals a weather/IROP cascade.' },
      { term: 'ADK Calls (24h)',   def: 'Same ADK count over 24 hours. Compare with 1h to spot surges vs. normal baseline.' },
    ],
  },
  {
    title: '⚡ ADK Skill Feed',
    items: [
      { term: 'Skill badge colors',  def: 'Blue=pnr_ingest · Purple=ava_insights · Red=irop_event_push · Orange=msp_dispatch · Teal=vuuoffer_push · Green=attestation_push · Gray=dispatch_status' },
      { term: 'LIVE badge',          def: 'Appears when new skill calls arrive via WebSocket without a page reload.' },
      { term: '⚠ risk number',       def: 'AVA congestion_index at invocation time. Red ≥ 0.6 = passenger flagged high-risk.' },
      { term: 'Colored dot',         def: 'Green = success · Orange = degraded · Red = error. Latency ms shown next to it.' },
    ],
  },
  {
    title: '🎯 Risk Distribution',
    items: [
      { term: 'Critical (≥ 0.70)',    def: 'Very likely to miss connection. Triggers HIGH_RISK_ALERT WebSocket broadcast.' },
      { term: 'High (0.50–0.69)',     def: 'Elevated risk; watch closely.' },
      { term: 'Moderate (0.30–0.49)',  def: 'Monitor but not yet actionable.' },
      { term: 'Low (< 0.30)',         def: 'Journey proceeding normally.' },
      { term: 'Carrier Volume bars',  def: 'PNR count per airline. B6=JetBlue · DL=Delta · UA=United · AA=American · WN=Southwest.' },
    ],
  },
  {
    title: '🚀 Dispatch Board',
    items: [
      { term: 'Queued',          def: 'Worker assigned in system; not yet moving.' },
      { term: 'Worker Assigned', def: 'A specific MSP worker has accepted the task.' },
      { term: 'En Route',        def: 'Worker moving to passenger anchor. ETA via OSRM.' },
      { term: 'Arrived',         def: 'Worker reached the anchor and is with the passenger.' },
      { term: 'Completed',       def: 'Task closed — passenger assisted or IROP resolved.' },
    ],
  },
  {
    title: '📡 Quick Access',
    items: [
      { term: 'channelSensor Ops',  def: 'Live ground-truth tiles per anchor: congestion bar, friction score, IROP flag. Refreshes every 15min.' },
      { term: 'Node Heat Map',      def: 'SVG floor plan JFK T5 + BOS T-C. Dots colored by congestion_index. Pulse = ≥ 0.7.' },
      { term: 'PNR Mutations',      def: 'IBM ADK rebooking log: original vs. new flight, Hilton hotel refs, National car refs, revenue delta.' },
      { term: 'Anc Revenue',         def: 'Actual ancillary revenue vs. vuuForge projected per carrier.' },
      { term: 'Carrier Metrics',    def: 'Per-carrier table: PNR volume, high-risk count, dispatch count, risk rate %.' },
      { term: 'Third-Party Panels', def: 'Sabre Agency360 · IBM Cloud Pak · Hilton CRS · National IDS. Tenant-scoped.' },
    ],
  },
  {
    title: '🔑 Glossary',
    items: [
      { term: 'PNR',              def: 'Passenger Name Record — 6-character reservation ID (CT-XXXXXX in cloudTraQ).' },
      { term: 'PAX',              def: 'Passenger — IATA aviation abbreviation.' },
      { term: 'IROP',             def: 'Irregular Operation — flight disruption (cancellation, delay, gate change).' },
      { term: 'AVA',              def: 'Automated Venue Analytics — IBM ADK AI engine scoring passengers 0–1 for disruption risk.' },
      { term: 'congestion_index', def: '0.0–1.0 AVA composite score: 0=clear, 0.3=moderate, 0.6=high risk, 0.7+=critical.' },
      { term: 'channelSensor',    def: 'Cisco venue telemetry: dwell time, foot traffic rate, device density. Powers AVA.' },
      { term: 'CloudMist',        def: 'cloudTraQ operator-to-passenger messaging layer. Send messages from any PNR detail page.' },
      { term: 'TenantScoped',     def: 'All queries filtered to the logged-in operator tenant.' },
    ],
  },
]

function HelpModal({ onClose }) {
  const [section, setSection] = useState(0)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center',
               justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
                    display: 'flex', width: 720, maxHeight: '86vh', overflow: 'hidden' }}>
        <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid #F3F4F6',
                      background: '#F9FAFB', padding: '16px 0', overflowY: 'auto' }}>
          <p style={{ padding: '0 16px 8px', fontSize: 10, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF' }}>
            Help Topics
          </p>
          {HELP_SECTIONS.map((s, i) => (
            <button key={i} onClick={() => setSection(i)} style={{
              width: '100%', textAlign: 'left', padding: '8px 16px',
              fontSize: 12, fontWeight: i === section ? 600 : 400,
              background: i === section ? '#EDE9FE' : 'transparent',
              color: i === section ? '#5B21B6' : '#4B5563',
              borderRight: i === section ? '2px solid #7C3AED' : '2px solid transparent',
              border: 'none', cursor: 'pointer',
            }}>
              {s.title}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '18px 22px 14px', borderBottom: '1px solid #F3F4F6',
                        position: 'sticky', top: 0, background: '#fff' }}>
            <h2 style={{ fontWeight: 700, color: '#111827', fontSize: 14, margin: 0 }}>{HELP_SECTIONS[section].title}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
                                               color: '#9CA3AF', fontSize: 22, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: '14px 22px' }}>
            {HELP_SECTIONS[section].items.map(({ term, def }) => (
              <div key={term} style={{ borderBottom: '1px solid #F9FAFB', paddingBottom: 10, marginBottom: 10 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>{term}</p>
                <p style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.55, margin: 0 }}>{def}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── SkillBadge ───────────────────────────────────────────────────────────────

function SkillBadge({ skill }) {
  const color = SKILL_COLORS[skill] || '#9E9E9E'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      backgroundColor: color + '22', color, border: `1px solid ${color}`,
    }}>
      {skill?.replace(/_/g, ' ') || '?'}
    </span>
  )
}

// ─── KpiTile ─────────────────────────────────────────────────────────────────

function KpiTile({ label, value, color, href, tip }) {
  const inner = (
    <div style={{
      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
      padding: '16px 12px', textAlign: 'center', cursor: href ? 'pointer' : 'default',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <p style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color, margin: 0 }}>{value ?? '—'}</p>
      <p style={{ fontSize: 11, color: '#6B7280', marginTop: 2, fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        {label}
        {tip && <InfoTip text={tip} position="bottom" />}
      </p>
    </div>
  )
  return href ? <a href={href} style={{ textDecoration: 'none' }}>{inner}</a> : inner
}

// ─── RiskDonut ───────────────────────────────────────────────────────────────

const RISK_TIPS = {
  critical: 'congestion_index ≥ 0.70 — very likely to miss connection. Triggers HIGH_RISK_ALERT.',
  high:     'congestion_index 0.50–0.69 — elevated risk, watch closely.',
  moderate: 'congestion_index 0.30–0.49 — monitor but not yet actionable.',
  low:      'congestion_index < 0.30 — journey proceeding normally.',
}

function RiskDonut({ distribution }) {
  const entries = Object.entries(distribution)
  const total   = entries.reduce((n, [, v]) => n + v, 0)
  if (total === 0) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 112, color: '#9CA3AF', fontSize: 12 }}>
      No PNR data
    </div>
  )

  const CX = 60, CY = 60, R = 48
  let angle = -Math.PI / 2
  const paths = entries.map(([key, count]) => {
    const slice = (count / total) * 2 * Math.PI
    const x1 = CX + R * Math.cos(angle), y1 = CY + R * Math.sin(angle)
    angle += slice
    const x2 = CX + R * Math.cos(angle), y2 = CY + R * Math.sin(angle)
    const large = slice > Math.PI ? 1 : 0
    const color = RISK_COLORS[key] || '#9E9E9E'
    return { key, count, color, d: `M${CX},${CY} L${x1.toFixed(1)},${y1.toFixed(1)} A${R},${R} 0 ${large},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z` }
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width={120} height={120} viewBox="0 0 120 120">
        {paths.map(p => <path key={p.key} d={p.d} fill={p.color} stroke="#fff" strokeWidth={2} />)}
        <circle cx={CX} cy={CY} r={24} fill="#fff" />
        <text x={CX} y={CY + 5} textAnchor="middle" fontSize={11} fontWeight="700" fill="#37474F">{total}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {entries.map(([key, count]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%',
                           background: RISK_COLORS[key] || '#9E9E9E', flexShrink: 0 }} />
            <span style={{ color: '#4B5563', textTransform: 'capitalize' }}>{key}</span>
            <InfoTip text={RISK_TIPS[key]} position="right" />
            <span style={{ marginLeft: 'auto', fontWeight: 700,
                           fontVariantNumeric: 'tabular-nums', color: RISK_COLORS[key] || '#9E9E9E' }}>
              {count}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── CarrierBars ─────────────────────────────────────────────────────────────

function CarrierBars({ breakdown }) {
  const entries = Object.entries(breakdown).sort((a, b) => b[1] - a[1])
  const max     = entries[0]?.[1] || 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {entries.map(([carrier, count]) => (
        <div key={carrier} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, width: 24, textAlign: 'right',
                         color: CARRIER_COLORS_M[carrier] || '#607D8B', flexShrink: 0 }}>
            {carrier}
          </span>
          <div style={{ flex: 1, background: '#F3F4F6', borderRadius: 999, height: 12, overflow: 'hidden' }}>
            <div style={{ width: `${(count / max) * 100}%`, height: '100%', borderRadius: 999,
                          background: CARRIER_COLORS_M[carrier] || '#607D8B' }} />
          </div>
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', width: 32, color: '#4B5563' }}>{count}</span>
          <span style={{ color: '#9CA3AF', fontSize: 11 }}>{CARRIER_NAMES[carrier] || ''}</span>
        </div>
      ))}
      {entries.length === 0 && <p style={{ color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>No carrier data</p>}
    </div>
  )
}

// ─── MiniSkillFeed ────────────────────────────────────────────────────────────

const SKILL_TIPS = {
  pnr_ingest:       'Creates or updates a CloudtraqPnr from a GDS/NDC source.',
  ava_insights:     'Runs AVA risk scoring on a PNR using channelSensor data.',
  irop_event_push:  'Propagates an IROP alert — highest urgency skill.',
  msp_dispatch:     'Creates a DispatchTask assigning an MSP worker to a passenger.',
  vuuoffer_push:    'Pushes a vuuOffer (upgrade, lounge, fast-track) to the passenger app.',
  attestation_push: 'Records a channelSensor venue attestation, updating congestion_index.',
  dispatch_status:  'Updates an existing DispatchTask status (en_route, arrived, completed).',
}

function MiniSkillFeed({ logs, liveEvents }) {
  const all = [...liveEvents.map(e => ({ ...e, _live: true })), ...logs].slice(0, 20)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto' }}>
      {all.length === 0 && (
        <p style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center', padding: '16px 0' }}>
          No recent skill calls
        </p>
      )}
      {all.map((log, i) => (
        <div key={log.id || `live-${i}`} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 8px', borderRadius: 6, fontSize: 12,
          background: log._live ? '#EFF6FF' : '#F9FAFB',
          border: log._live ? '1px solid #BFDBFE' : '1px solid transparent',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <SkillBadge skill={log.skill} />
            {SKILL_TIPS[log.skill] && <InfoTip text={SKILL_TIPS[log.skill]} position="right" />}
          </div>
          {log.pnr && (
            <a href={`/admin/cloudtraq_gds/pnr/${log.pnr}`}
               style={{ fontFamily: 'monospace', color: '#4F46E5', textDecoration: 'none', flexShrink: 0 }}>
              {log.pnr}
            </a>
          )}
          {log.carrier && <span style={{ fontFamily: 'monospace', color: '#6B7280', flexShrink: 0 }}>{log.carrier}</span>}
          {log.risk != null && log.risk >= 0.6 && (
            <span style={{ color: '#EF4444', fontWeight: 700, flexShrink: 0 }}>⚠ {log.risk}</span>
          )}
          {log.latency_ms != null && (
            <span style={{ color: '#9CA3AF', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{log.latency_ms}ms</span>
          )}
          <span style={{ marginLeft: 'auto', color: STATUS_COLORS[log.status] || '#9E9E9E', flexShrink: 0 }}>●</span>
          <span style={{ color: '#9CA3AF', flexShrink: 0 }}>{timeAgo(log.occurred_at)}</span>
        </div>
      ))}
    </div>
  )
}

// ─── DispatchSummary ─────────────────────────────────────────────────────────

const STATUS_ORDER = ['queued', 'worker_assigned', 'en_route', 'arrived', 'completed']
const STATUS_META  = {
  queued:          { label: 'Queued',    color: '#FF9800', tip: 'Worker assigned in system; not yet moving.' },
  worker_assigned: { label: 'Assigned',  color: '#2196F3', tip: 'A specific MSP worker has accepted the task.' },
  en_route:        { label: 'En Route',  color: '#9C27B0', tip: 'Worker moving to passenger anchor. ETA via OSRM.' },
  arrived:         { label: 'Arrived',   color: '#00BCD4', tip: 'Worker reached the anchor and is with the passenger.' },
  completed:       { label: 'Completed', color: '#4CAF50', tip: 'Task closed — passenger assisted or IROP resolved.' },
}

function DispatchSummary({ byStatus }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {STATUS_ORDER.map(s => {
        const count = byStatus[s] || 0
        const meta  = STATUS_META[s] || { label: s, color: '#9E9E9E', tip: '' }
        return (
          <div key={s} style={{ flex: 1, minWidth: 56, background: '#F9FAFB',
                                border: '1px solid #E5E7EB', borderRadius: 10,
                                padding: '12px 8px', textAlign: 'center' }}>
            <p style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: meta.color, margin: 0 }}>
              {count}
            </p>
            <p style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 2, margin: '2px 0 0' }}>
              {meta.label}
              <InfoTip text={meta.tip} position="top" />
            </p>
          </div>
        )
      })}
    </div>
  )
}

// ─── QuickNavCard ─────────────────────────────────────────────────────────────

const QUICK_NAV_TIPS = {
  '/admin/cloudtraq_gds/channel_sensor':    'Live ground-truth tiles per anchor: congestion bar, friction score, IROP flag. Refreshes every 15min via ChannelSensorSnapshotJob.',
  '/admin/cloudtraq_gds/node_heat_map':     'SVG floor plan JFK T5 + BOS T-C. Dots colored by congestion_index. Pulse = ≥ 0.7. Purple badge = active dispatch.',
  '/admin/cloudtraq_gds/pnr_mutations':     'IBM ADK rebooking log: original vs. new flight, Hilton hotel refs, National car refs, revenue delta.',
  '/admin/cloudtraq_gds/anc_revenue':        'Actual ancillary revenue vs. vuuForge projected per carrier.',
  '/admin/cloudtraq_gds/carrier_metrics':   'Per-carrier: PNR volume bar, high-risk count, dispatch count, risk rate %. ≥ 20% = red.',
  '/admin/cloudtraq_gds/third_party/sabre': 'Sabre · IBM · Hilton · National tabs. All tenant-scoped; unauthorized shows a lock.',
}

function QuickNavCard({ href, icon, label, sub }) {
  const tip = QUICK_NAV_TIPS[href]
  return (
    <a
      href={href}
      style={{ display: 'block', borderRadius: 8, border: '1px solid #E5E7EB',
               padding: '10px 12px', textDecoration: 'none',
               transition: 'border-color 0.15s, background 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#93C5FD'; e.currentTarget.style.background = '#EFF6FF' }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        {tip && <span onClick={e => e.preventDefault()}><InfoTip text={tip} position="top" /></span>}
      </div>
      <p style={{ fontSize: 12, fontWeight: 600, color: '#1F2937', margin: '4px 0 2px' }}>{label}</p>
      <p style={{ fontSize: 11, color: '#9CA3AF', margin: 0 }}>{sub}</p>
    </a>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Index({ kpis, risk_distribution, dispatch_by_status, carrier_breakdown, recent_skill_feed, tenant_branding }) {
  const intervalRef               = useRef(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [liveEvents, setLiveEvents]   = useState([])
  const [helpOpen, setHelpOpen]       = useState(false)

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        router.reload({ only: ['kpis', 'risk_distribution', 'dispatch_by_status', 'carrier_breakdown', 'recent_skill_feed'] })
      }, 30_000)
    } else {
      clearInterval(intervalRef.current)
    }
    return () => clearInterval(intervalRef.current)
  }, [autoRefresh])

  useOpsMonitor({
    tenantKey: tenant_branding?.tenant_key || 'cloudtraq',
    onEvent: useCallback((event, payload) => {
      if (event === 'SKILL_INVOKED' && payload) {
        setLiveEvents(prev => [{ ...payload, _live: true }, ...prev].slice(0, 30))
      }
      if (event === 'HIGH_RISK_ALERT') router.reload({ only: ['kpis'] })
    }, []),
  })

  const brand    = tenant_branding || {}
  const hasRisks = Object.values(risk_distribution).some(v => v > 0)

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: '24px 20px' }}>
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {brand.logo_url && <img src={brand.logo_url} alt={brand.display_name} style={{ height: 32, width: 'auto' }} />}
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
              cloudTraQ GDS Ops
              {brand.display_name && brand.display_name !== 'cloudTraQ' && (
                <span style={{ fontSize: 15, color: '#9CA3AF', fontWeight: 400, marginLeft: 8 }}>
                  — {brand.display_name}
                </span>
              )}
            </h1>
            <p style={{ fontSize: 11, color: '#6B7280', margin: '2px 0 0' }}>
              IBM watsonx ADK Command Center · {kpis.skill_calls_1h} calls (1h) · {kpis.active_pnr_count} PNRs active
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => setHelpOpen(true)}
            style={{ padding: '5px 12px', fontSize: 12, border: '1px solid #C4B5FD',
                     borderRadius: 6, background: '#EDE9FE', color: '#5B21B6', cursor: 'pointer' }}
          >
            ? Help
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button
            onClick={() => router.reload()}
            style={{ padding: '5px 12px', fontSize: 12, border: '1px solid #D1D5DB',
                     borderRadius: 6, background: '#fff', cursor: 'pointer' }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        <KpiTile label="Active PNRs"       value={kpis.active_pnr_count} color="#2196F3"
          href="/admin/cloudtraq_gds/high_risk"
          tip="Total cloudTraQ PNR records in the system. Click to see high-risk ones." />
        <KpiTile label="High Risk"          value={kpis.high_risk_count}
          color={kpis.high_risk_count > 0 ? '#F44336' : '#9E9E9E'}
          href="/admin/cloudtraq_gds/high_risk"
          tip="PNRs where AVA congestion_index > 0.6. Passengers likely to miss a connection." />
        <KpiTile label="Active Dispatches" value={kpis.active_dispatches} color="#FF9800"
          href="/admin/cloudtraq_gds/dispatch_board"
          tip="MSP worker dispatch tasks in progress. Click to open the Kanban board." />
        <KpiTile label="ADK Calls (1h)"    value={kpis.skill_calls_1h}   color="#9C27B0"
          href="/admin/cloudtraq_gds/skill_feed"
          tip="IBM watsonx ADK skill calls in the last hour. A spike often signals an IROP cascade." />
        <KpiTile label="ADK Calls (24h)"   value={kpis.skill_calls_24h}  color="#607D8B"
          href="/admin/cloudtraq_gds/skill_feed"
          tip="ADK calls over 24 hours. Compare with 1h to distinguish surge from baseline." />
      </div>

      {/* 4-quadrant grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Q1: Skill Feed */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
                        letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              ⚡ ADK Skill Feed
              <InfoTip text="Real-time IBM watsonx ADK skill calls. Blue rows are live WebSocket injections — no reload needed. Click any PNR locator for the full drill-down." position="right" />
              {liveEvents.length > 0 && (
                <span style={{ background: '#3B82F6', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 999 }}>
                  LIVE
                </span>
              )}
            </p>
            <a href="/admin/cloudtraq_gds/skill_feed" style={{ fontSize: 12, color: '#3B82F6', textDecoration: 'none' }}>All →</a>
          </div>
          <MiniSkillFeed logs={recent_skill_feed} liveEvents={liveEvents} />
        </div>

        {/* Q2: Risk + Carrier */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
                      letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            🎯 Risk Distribution
            <InfoTip text="AVA congestion_index buckets for all PNRs. Hover each legend row for exact thresholds. Number in center = total PNR count." position="left" />
          </p>
          {hasRisks ? <RiskDonut distribution={risk_distribution} />
            : <p style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', padding: '8px 0' }}>No congestion data</p>}
          <div style={{ borderTop: '1px solid #F3F4F6', marginTop: 16, paddingTop: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
                        letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              ✈ Carrier Volume
              <InfoTip text="PNR count per airline. Bar length relative to highest-volume carrier. B6=JetBlue · DL=Delta · UA=United · AA=American · WN=Southwest." position="left" />
            </p>
            <CarrierBars breakdown={carrier_breakdown} />
          </div>
        </div>

        {/* Q3: Dispatch */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
                        letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              🚀 Dispatch Board
              <InfoTip text="MSP worker dispatch tasks grouped by lifecycle status. Open the Kanban to advance tasks or check worker ETAs." position="right" />
            </p>
            <a href="/admin/cloudtraq_gds/dispatch_board" style={{ fontSize: 12, color: '#3B82F6', textDecoration: 'none' }}>Open →</a>
          </div>
          <DispatchSummary byStatus={dispatch_by_status} />
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #F3F4F6', display: 'flex', gap: 8 }}>
            <a href="/admin/cloudtraq_gds/dispatch_board"
              style={{ fontSize: 11, padding: '5px 12px', background: '#FFF7ED', border: '1px solid #FED7AA',
                       color: '#C2410C', borderRadius: 6, textDecoration: 'none' }}>
              Full Kanban Board
            </a>
            <a href="/admin/cloudtraq_gds/high_risk"
              style={{ fontSize: 11, padding: '5px 12px', background: '#FEF2F2', border: '1px solid #FECACA',
                       color: '#B91C1C', borderRadius: 6, textDecoration: 'none' }}>
              High Risk Candidates
            </a>
          </div>
        </div>

        {/* Q4: Quick Access */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
                      letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            📡 Quick Access
            <InfoTip text="Shortcuts to all 6 deep-dive pages. Hover ⓘ on each card before clicking to preview what that page shows." position="left" />
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { href: '/admin/cloudtraq_gds/channel_sensor',    icon: '📡', label: 'channelSensor Ops',  sub: 'Ground truth AVA snapshots' },
              { href: '/admin/cloudtraq_gds/node_heat_map',      icon: '🗺️', label: 'Node Heat Map',      sub: 'Airport anchor congestion' },
              { href: '/admin/cloudtraq_gds/pnr_mutations',      icon: '✈️', label: 'PNR Mutations',      sub: 'GDS/CRS rebooking log' },
              { href: '/admin/cloudtraq_gds/anc_revenue',         icon: '💰', label: 'Anc Revenue',         sub: 'Projected vs. actual' },
              { href: '/admin/cloudtraq_gds/carrier_metrics',    icon: '📈', label: 'Carrier Metrics',    sub: 'Per-carrier stats' },
              { href: '/admin/cloudtraq_gds/third_party/sabre',  icon: '🏢', label: 'Third-Party Panels', sub: 'Sabre · IBM · Hilton · National' },
            ].map(card => <QuickNavCard key={card.href} {...card} />)}
          </div>
        </div>

      </div>
    </div>
  )
}
